import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { IToggleOption } from '@rbilabs/components-library/build/components/toggle';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { useRoutes } from 'hooks/use-routes';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import { isNotNull } from 'utils/is-not-null';
import { routes } from 'utils/routing';
import { ServiceModeCategory } from 'utils/service-mode';

export interface IUseServiceModeCategoryResult {
  serviceModeCategory: ServiceModeCategory;
  setServiceModeCategory: Dispatch<SetStateAction<ServiceModeCategory>>;
  serviceModeCategoryOptions: IToggleOption[];
}

export interface IUseServiceModeCategory {
  (): IUseServiceModeCategoryResult;
}

export const useServiceModeCategory: IUseServiceModeCategory = () => {
  const isDeliveryEnabled = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  const isOnlyDeliveryEnabled = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_ONLY);
  const location = useLocation();
  const { getLocalizedRouteForPath } = useRoutes();
  const { serviceMode } = useServiceModeContext();

  const { formatMessage } = useIntl();

  const serviceModeCategoryOptions = useMemo(() => {
    if (isOnlyDeliveryEnabled) {
      return [{ label: formatMessage({ id: 'delivery' }), value: ServiceModeCategory.DELIVERY }];
    }

    return [
      { label: formatMessage({ id: 'pickUp' }), value: ServiceModeCategory.PICKUP },
      isDeliveryEnabled
        ? { label: formatMessage({ id: 'delivery' }), value: ServiceModeCategory.DELIVERY }
        : null,
    ].filter(isNotNull);
  }, [formatMessage, isDeliveryEnabled, isOnlyDeliveryEnabled]);

  const [serviceModeCategory, setServiceModeCategory] = useState(
    serviceModeCategoryOptions[0]?.value || ServiceModeCategory.PICKUP
  );

  // Keep the service mode category tabs in sync with the related routes.
  useEffect(() => {
    // Main pickup route.
    if (location.pathname === routes.storeLocator) {
      setServiceModeCategory(ServiceModeCategory.PICKUP);
    }

    // Delivery route.
    if (location.pathname === routes.address) {
      setServiceModeCategory(ServiceModeCategory.DELIVERY);
    }
  }, [getLocalizedRouteForPath, location.pathname, serviceMode]);

  return {
    serviceModeCategory,
    setServiceModeCategory,
    serviceModeCategoryOptions,
  };
};
